<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <b-img
            style="width: 200px;"
            :src="require('@/assets/images/logo/logo.png')"
          />
        </b-link>

        <b-card-title class="mb-1">
          Bem vindo! 👋
        </b-card-title>
        <div
          class="d-flex flex-column align-items-center"
          style="margin-bottom: 2rem; margin-top: 2rem;"
        >
          <img
            :src="logoReceitaFederal"
            alt="Logo Receita Federal"
            style="width: 70px; height: auto; margin-bottom: 15px"
          >
          <h3 style="color: #003473; font-weight: 600; -webkit-text-stroke: 1px #003473; text-align: center;">
            Somos uma plataforma homologada pela Receita Federal
          </h3>
        </div>
        <b-card-text class="mb-2">
          Faça login na sua conta e acesse o portal
        </b-card-text>

        <validation-observer
          ref="validacao"
          #default="{ invalid }"
        >
          <b-form
            class="auth-login-form mt-2"
            @submit.prevent
          >
            <b-form-group
              label-for="email"
              label="E-mail"
            >
              <validation-provider
                #default="{ errors }"
                name="Email"
                rules="required|email"
              >
                <b-form-input
                  id="email"
                  v-model="form.login"
                  name="login-email"
                  :state="errors.length > 0 ? false : null"
                  placeholder="contato@empresa.com.br"
                  autofocus
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group>
              <div class="d-flex justify-content-between">
                <label for="password">Senha</label>
              </div>
              <senha-input
                :id="'senhaInput'"
                v-model="form.senha"
                :required="true"
                :validacao-nome="'Password'"
              />
              <div class="d-flex justify-content-between">
                <b-link :to="{ name: 'auth-forgot-password-v1' }">
                  <small
                    style="display: flex; margin-top: 0.5rem;"
                    @click="esqueceuSenha"
                  >Esqueceu a senha?</small>
                </b-link>
              </div>
            </b-form-group>
            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="invalid || isLoading"
              @click="autenticar"
            >
              <b-spinner
                v-if="isLoading"
                small
              />
              <span>{{ isLoading ? 'Entrando...' : 'Entrar' }}</span>
            </b-button>
          </b-form>
        </validation-observer>
      </b-card>
    </div>
    <loading
      class="loading"
      :show-cancel-button="false"
      :is-loading="isLoading"
      :titulo-carregamento="'Aguarde!'"
    />
  </div>
</template>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";

.loading {
  .loading_titulo {
    display: flex;
    height: 12%;
  }

  .loading_descricao {
    display: flex;
    height: 14%;
  }
}
</style>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
// eslint-disable-next-line import/no-cycle
import {
  updateAbility, clearLoggedIn,
} from '@/auth/utils'
// eslint-disable-next-line import/no-cycle
import useJwt from '@/auth/jwt/useJwt'
import eventHub, { verificarTermosPendentesLogin } from '@/app/shared/hubs/eventHub'
import Vue from 'vue'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    SenhaInput: () => import('@pilar/components/senha-input/SenhaInput.vue'),
    Loading: () => import('@pilar/components/loading/Loading.vue'),
  },
  data() {
    return {
      form: {
        login: '',
        senha: '',
      },
      esqueci: null,
      loginError: '',
      required,
      email,
      // eslint-disable-next-line global-require
      logoReceitaFederal: require('@/assets/images/logo/receita_federal.png'),
      payloadTemp: null,
      isLoading: false,
    }
  },
  created() {
    clearLoggedIn()
    // para conexão com a hub (para evitar conexão com usuário antigo ao trocar de usuário)
    const connection = eventHub()
    connection.stop()

    // Verificar se há algum retorno da validação em duas etapas
    if (this.$route.params.validacaoSucesso === true) {
      const savedPayload = localStorage.getItem('auth_payload')
      if (savedPayload) {
        const payload = JSON.parse(savedPayload)
        this.finalizarLogin(payload)
        localStorage.removeItem('auth_payload')
      }
    }
  },
  methods: {
    autenticar() {
      this.loginError = ''
      this.$refs.validacao.validate()
        .then(() => {
          this.isLoading = true
          this.form.login = this.form.login.trim()
          useJwt.login(this.form)
            .then(payload => {
              if (payload.data.flgValidacaoDuasEtapas) {
                const { usuarioId } = payload.data.userData
                localStorage.setItem('auth_payload', JSON.stringify(payload.data))
                localStorage.setItem('user_email', this.form.login)
                const destino = '/acesso/validacao-duas-etapas'
                if (this.$route.path !== destino) {
                  this.$router.push({
                    name: 'validacao-duas-etapas',
                    params: { usuarioId, email },
                  })
                }
              } else {
                this.finalizarLogin(payload.data)
              }
            })
            .catch(error => {
              this.isLoading = false
              const errorMessage = error.response?.data?.message || ''
              if (errorMessage.includes('Redis error')) {
                this.loginError = 'Sistema indisponível no momento, tente novamente em alguns instantes. Se o problema persistir contate o Administrador.'
              } else if (errorMessage) {
                this.loginError = error.response.data.message
              } else {
                this.loginError = 'Erro ao tentar acessar o sistema. Contate o administrador!'
              }
              this.$refs.validacao.setErrors({ Password: this.loginError })
              throw error
            })
        })
    },
    finalizarLogin(payloadData) {
      useJwt.setUserData(payloadData.userData)
      useJwt.setToken(payloadData.accessToken)
      useJwt.setPermissoesPlano(payloadData.permissaoPlano)

      updateAbility(this.$ability)

      eventHub()
      verificarTermosPendentesLogin()

      this.isLoading = false
      this.$router.push({ name: 'dashboard-analitico', params: { primeiroAcesso: true } })
    },
    forgotPassword() {
      this.form.login = this.form.login.trim()
      useJwt.esqueciSenha(this.form.login)
        .then(() => {
          this.esqueci = {
            message: `E-mail enviado com sucesso para ${this.form.login}`,
            error: false,
          }
        })
        .catch(err => {
          const msg = (!err.message || !err.response.data)
            ? 'Não foi possível enviar o email de recuperação de senha. Contate o administrador'
            : err.response.data.message

          this.esqueci = {
            message: msg,
            error: true,
          }
        })
    },
    esqueceuSenha() {
      useJwt.forgotPassword(this.form)
        .then(payload => {
          if (payload.data === true) {
            Vue.swal({
              title: 'Redefinir senha',
              text: 'Encaminhamos um e-mail para redefinir a sua senha!',
              icon: 'warning',
              showCancelButton: false,
              confirmButtonText: 'Ok',
              customClass: {
                confirmButton: 'btn btn-outline-primary mr-50',
              },
              buttonsStyling: false,
            })
          }
        })
        .catch(error => {
          this.loginError = error.response?.data ? error.response.data.message : 'Erro ao tentar acessar o sistema. Contate o administrador!'
          this.$refs.validacao.setErrors({ email: this.loginError })
          Vue.swal({
            title: 'Ocorrem um problema',
            text: error.response.data.message,
            icon: 'warning',
            iconColor: 'sucesso',
            showCancelButton: false,
            confirmButtonText: 'Ok',
            customClass: {
              confirmButton: 'btn btn-outline-primary mr-50',
            },
            buttonsStyling: false,
          })
        })
    },
  },
}
</script>
